import { MediaElement, MediaImage, PrintTechnology, Spread } from 'editor/src/store/design/types';
import { ElementWarning, WarningLevel, WarningType } from 'editor/src/store/editorModules/warnings/types';
import { GalleryImage, ImagePrintDetail } from 'editor/src/store/gallery/types';
import hasCurrentPrintDetail from 'editor/src/store/gallery/utils/hasCurrentPrintDetail';
import { EnabledWarnings, PrintTechRules } from 'editor/src/store/hostSettings/types';

import hasInk from 'editor/src/util/color/hasInk';
import HSVA from 'editor/src/util/color/hsvUtils';
import RGBA from 'editor/src/util/color/rgbaUtils';
import { MAX_MEASURED_WIDTH } from 'editor/src/util/printTechAnalysis/analyzePrintDetail';
import pt2mm from 'editor/src/util/pt2mm';

import { getPatternRepeat } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Image/useFilters';

// Soft semi-transparent areas above this share of the visible artwork print as a halo on DTF.
export const SEMI_TRANSPARENT_LIMIT = 0.03;
// Share of the artwork in smooth gradients (measureGradientShare on the MAX_ANALYSIS_SIZE raster):
// flat logos, JPEG and grainy ones included, stay under about 0.12; a gradient reads about the share
// of the artwork it covers.
export const GRADIENT_LIMIT = 0.2;
// Share of the artwork in too-thin parts; below it the parts are stray pixels nobody would miss.
export const FINE_DETAIL_LIMIT = 0.002;

type Check =
  | WarningType.SemiTransparentPixels
  | WarningType.FineDetail
  | WarningType.SmallText
  | WarningType.ThinLine
  | WarningType.Gradient;

// Rounded down, so a size just under the minimum never reads as the minimum itself.
const floorToHundredth = (mm: number) => Math.floor(mm * 100 + 1e-9) / 100;

// The image checks that run on a spread of this technology, all reading the image's measured pixels.
function getImageChecks(
  technology: PrintTechnology | undefined,
  enabledWarnings: Pick<EnabledWarnings, Check>,
  printTechRules: PrintTechRules | undefined,
) {
  return {
    semiTransparent: enabledWarnings.SemiTransparentPixels && technology === PrintTechnology.DTF,
    gradient: enabledWarnings.Gradient && technology === PrintTechnology.Embroidery,
    fineDetailMinMm:
      enabledWarnings.FineDetail && technology ? printTechRules?.[technology]?.minLineWidthMm : undefined,
  };
}

export function needsImagePrintDetail(
  technology: PrintTechnology | undefined,
  enabledWarnings: Pick<EnabledWarnings, Check>,
  printTechRules: PrintTechRules | undefined,
): boolean {
  const checks = getImageChecks(technology, enabledWarnings, printTechRules);
  return checks.semiTransparent || checks.gradient || !!checks.fineDetailMinMm;
}

function getThinRatio(detail: ImagePrintDetail, imageWidthMm: number, minLineWidthMm: number): number {
  if (!detail.thinRatioByWidth.length || imageWidthMm <= 0) {
    return 0;
  }
  const widthInRasterPixels = (minLineWidthMm * detail.rasterWidth) / imageWidthMm;
  // Under two raster pixels a part that thin can't be told from anti-aliasing, so the check stays silent.
  if (widthInRasterPixels < 2) {
    return 0;
  }
  // A part is a whole number of raster pixels wide, so one thinner than 5.5 px is one under 6 px.
  return detail.thinRatioByWidth[Math.min(Math.ceil(widthInRasterPixels), MAX_MEASURED_WIDTH)] ?? 0;
}

// A pattern prints the image once per tile, each tile `repeat` times smaller (as getImageDPI reads it).
function getPrintedImageWidthMm(image: MediaImage): number {
  if (!image.pattern) {
    return image.pw;
  }
  const { scale, scaleBase } = image.pattern;
  return image.pw / getPatternRepeat(scale, scaleBase, image.width, image.height, image.pw, image.ph);
}

// A colour with no opacity prints nothing, whether written as hex, rgba() or hsva().
const printsColour = (colour: string | undefined): colour is string =>
  hasInk(colour) && (RGBA.fromString(colour!) ?? HSVA.fromString(colour!)?.toRGB())?.a !== 0;

function getLineWidthMm(element: MediaElement): number | undefined {
  // The fill is painted over the outline's inner half, in the editor and in the print file alike.
  if (element.type === 'text' && element.extra.stroke?.width && printsColour(element.extra.stroke.color)) {
    return (element.extra.stroke.width * element.extra.fontSize * Math.abs(element.sy ?? 1)) / 2;
  }
  // "No outline" keeps the stroke colour and sets the width to 0, so a stroke prints only with both.
  if (
    (element.type === 'line' || element.type === 'rectangle') &&
    element.strokeWidth > 0 &&
    printsColour(element.stroke)
  ) {
    return element.strokeWidth;
  }
  return undefined;
}

/**
 * The print checks a print partner turns on: they only warn, and only on the spread's technology
 * they apply to. Sizes are in mm on the printed product.
 */
function getPrintRuleWarnings(
  element: MediaElement,
  spread: Spread,
  spreadIndex: number,
  galleryImage: GalleryImage | undefined,
  enabledWarnings: Pick<EnabledWarnings, Check>,
  printTechRules: PrintTechRules | undefined,
): ElementWarning[] {
  const warnings: ElementWarning[] = [];
  const base = { uuid: element.uuid, spreadIndex, level: WarningLevel.Low };
  const rules = spread.technology ? printTechRules?.[spread.technology] : undefined;

  // a sample image is a placeholder the customer replaces, as for the DPI check; an empty frame prints nothing
  const imageElement = element.type === 'image' && !element.sample && element.imageId ? element : undefined;
  // a detail measured on the upload preview, or on an image since replaced, is not this image's
  const printDetail = galleryImage && hasCurrentPrintDetail(galleryImage) ? galleryImage.printDetail : undefined;
  const imageChecks = getImageChecks(spread.technology, enabledWarnings, printTechRules);
  if (imageElement && imageChecks.semiTransparent) {
    // the element's own opacity and soft edge print as the same halo as see-through pixels
    const fadedByElement =
      (imageElement.opacity !== undefined && imageElement.opacity > 0 && imageElement.opacity < 1) ||
      (imageElement.feather ?? 0) > 0;
    if (fadedByElement || (printDetail && printDetail.semiTransparentRatio > SEMI_TRANSPARENT_LIMIT)) {
      warnings.push({ ...base, type: WarningType.SemiTransparentPixels });
    }
  }

  if (imageElement && printDetail) {
    if (imageChecks.gradient && printDetail.gradientRatio > GRADIENT_LIMIT) {
      warnings.push({ ...base, type: WarningType.Gradient });
    }
    const { fineDetailMinMm } = imageChecks;
    if (
      fineDetailMinMm &&
      getThinRatio(printDetail, getPrintedImageWidthMm(imageElement), fineDetailMinMm) > FINE_DETAIL_LIMIT
    ) {
      warnings.push({ ...base, type: WarningType.FineDetail, minLineWidthMm: fineDetailMinMm });
    }
  }

  // an empty text, such as a personalization field not filled in yet, prints nothing
  const isEmptyText = element.type === 'text' && !element.extra.text?.trim();
  if (enabledWarnings.SmallText && element.type === 'text' && !isEmptyText && rules?.minTextHeightMm) {
    const textHeightMm = pt2mm(element.extra.fontSize * Math.abs(element.sy ?? 1));
    if (textHeightMm < rules.minTextHeightMm) {
      warnings.push({
        ...base,
        type: WarningType.SmallText,
        textHeightMm: floorToHundredth(textHeightMm),
        minTextHeightMm: rules.minTextHeightMm,
      });
    }
  }

  const lineWidthMm = getLineWidthMm(element);
  if (enabledWarnings.ThinLine && lineWidthMm !== undefined && !isEmptyText && rules?.minLineWidthMm) {
    if (lineWidthMm < rules.minLineWidthMm) {
      warnings.push({
        ...base,
        type: WarningType.ThinLine,
        lineWidthMm: floorToHundredth(lineWidthMm),
        minLineWidthMm: rules.minLineWidthMm,
      });
    }
  }

  return warnings;
}

export default getPrintRuleWarnings;
